import { useCallback, useEffect, useMemo, useState } from "react";
import { RangeSlider, NumericInput, Props, Intent } from "@blueprintjs/core";
import { isEmpty } from "lodash";
import { applyFunction, evaluate } from "components/Template";
import { IMetadata, IModel, IMutators, IRange } from "components/Template/types";
import { doDisabled, doInvalid } from "components/Template/utils";
import Override from "components/Template/Override";
import Validate from "components/Template/Validate";
import ChangeHighlight from "components/Template/ChangeHighlight";
import "./Range.scss";

export interface IRangeProps extends Props {
  template: IRange;
  model: IModel;
  mutators: IMutators;
  metadata: IMetadata;
}

export const checkValue = (range: [number | undefined, number | undefined], v: [number, number]) => {
  let fault = "";

  const [min, max] = range;

  try {
    v.forEach((t) => {
      if (!isFinite(t)) {
        fault = `Entered value must be a valid number.`;
      }
      if (typeof min === 'number' && isFinite(min) && t < min) {
        fault = `Entered value must be equal or greater than ${min}`;
      }
      if (typeof max === 'number' && isFinite(max) && t > max) {
        fault = `Entered value must be equal or less than ${max}`;
      }
      if (v[0] > v[1]) {
        fault = "Lower limit needs to be smaller than upper limit";
      }
    });
  } catch (e) {
    fault = `Entered value must be a valid number.`;
  }
  return fault;
};

const Range = (props: IRangeProps) => {
  const { template, model, mutators, metadata } = props;
  const { getValue, handleChange, isHidden } = mutators;
  const [fault, setFault] = useState("");
  const faulted = !isEmpty(fault);

  const min = useMemo(() => {
    let resolvedMin: number | undefined = undefined;
    if (typeof template.min === 'object') {
      resolvedMin = applyFunction(model, template.min, getValue) as number;
    } else {
      resolvedMin = template.min;
    }
    if (resolvedMin === undefined && template.variant !== 'slider') {
      return -Infinity;
    }
    return resolvedMin;
  }, [getValue, model, template.min, template.variant]);

  const max = useMemo(() => {
    let resolvedMax: number | undefined = undefined;
    if (typeof template.max === 'object') {
      resolvedMax = applyFunction(model, template.max, getValue) as number;
    } else {
      resolvedMax = template.max;
    }
    if (resolvedMax === undefined && template.variant !== 'slider') {
      return Infinity;
    }
    return resolvedMax;
  }, [getValue, model, template.max, template.variant]);

  const [v1, v2] = getValue(template.name) as [number, number];
  const transformLowerInput = useCallback(
    (v: number) => {
      const f = checkValue([min, max], [v, v2]);
      setFault(f);
      return [v, v2];
    },
    [min, max, v2]
  );

  const transformUpperInput = useCallback(
    (v: number) => {
      const f = checkValue([min, max], [v1, v]);
      setFault(f);
      return [v1, v];
    },
    [min, max, v1]
  );

  useEffect(() => {
    if (!handleChange) return;

    const isMinFinite = typeof min === 'number' && isFinite(min);
    const isMaxFinite = typeof max === 'number' && isFinite(max);

    const clippedLowerValue = isMinFinite ? Math.max(min, Math.min(v1, isMaxFinite ? max : Infinity)) : v1;
    const clippedUpperValue = isMaxFinite ? Math.max(isMinFinite ? min : -Infinity, Math.min(v2, max)) : v2;

    if (clippedLowerValue !== v1 || clippedUpperValue !== v2) {
      handleChange(template.name, [clippedLowerValue, clippedUpperValue])
    }
  }, [min, max, handleChange, v1, v2, template.name]);

  if ((isHidden && isHidden(template.name)) || (template.hidden && evaluate(model, template.hidden, getValue))) {
    if (!metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput.push(template.name);
    }
    return null;
  } else {
    if (metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput = metadata.hiddenInput.filter((i) => i === template.name);
    }
  }

  const disabled = doDisabled(props);
  const invalid = doInvalid(props);

  const sliderMin = min ?? template.value[0];
  const sliderMax = max ?? template.value[1];

  const storedValue = mutators.getStoredValue ? mutators.getStoredValue(template.name) : undefined;
  const storedRange = Array.isArray(storedValue) ? (storedValue as [number, number]) : undefined;
  const hasChanged =
    storedRange !== undefined && Array.isArray(storedRange) &&
    (storedRange[0] !== v1 || storedRange[1] !== v2);

  const handleUndo = () => {
    if (storedRange && mutators.setStoredValue) {
      handleChange && handleChange(template.name, storedRange);
    }
  };

  const handleKeep = () => {
    if (mutators.setStoredValue) {
      mutators.setStoredValue(template.name, [v1, v2]);
    }
  };

  return (
    <Validate
      {...props}
      className="number"
      intent={faulted ? Intent.DANGER : Intent.NONE}
      helperText={faulted ? fault : ""}
      showLabel
      showInfo
    >
      <ChangeHighlight
        changed={hasChanged}
        onUndo={hasChanged ? handleUndo : undefined}
        onKeep={hasChanged ? handleKeep : undefined}
        changePath={mutators.getPath(template.name)}
      >
        <div className="range-container">
          {template?.variant === "slider" ? (
            <RangeSlider
              min={sliderMin}
              max={sliderMax}
              stepSize={template.step}
              labelStepSize={Math.floor((sliderMax - sliderMin) / 4)}
              {...(handleChange
                ? {
                    onChange: (v) => handleChange(template.name, v),
                    disabled: disabled,
                  }
                : {
                    onChange: () => {
                      /** disabled */
                    },
                    readOnly: true,
                  })}
              value={[v1, v2]}
              vertical={template.vertical}
            />
          ) : (
            <span className="range-text-container">
              <div className="input-with-button">
                <NumericInput
                  className="range-textfield"
                  large
                  key={template.name}
                  intent={invalid || faulted ? Intent.DANGER : Intent.NONE}
                  id={template.name}
                  min={typeof min === 'number' && isFinite(min) ? min : undefined}
                  max={typeof max === 'number' && isFinite(max) ? max : undefined}
                  stepSize={template.step}
                  minorStepSize={null}
                  majorStepSize={null}
                  value={v1}
                  {...(handleChange
                    ? {
                        onValueChange: (v: number) => handleChange(template.name, transformLowerInput(v)),
                      }
                    : { readOnly: true })}
                  disabled={doDisabled(props)}
                />
                {template.info ? <span className="unit"> {template.info}</span> : ""}
              </div>
              <span className="divider"></span>
              <div className="input-with-button">
                <NumericInput
                  className="range-textfield"
                  key={template.name}
                  intent={invalid || faulted ? Intent.DANGER : Intent.NONE}
                  id={template.name}
                  min={typeof min === 'number' && isFinite(min) ? min : undefined}
                  max={typeof max === 'number' && isFinite(max) ? max : undefined}
                  stepSize={template.step}
                  minorStepSize={null}
                  majorStepSize={null}
                  value={v2}
                  {...(handleChange
                    ? {
                        onValueChange: (v: number) => handleChange(template.name, transformUpperInput(v)),
                      }
                    : { readOnly: true })}
                  disabled={doDisabled(props)}
                />
                {template.info ? <span className="unit"> {template.info}</span> : ""}
              </div>
            </span>
          )}
          <Override {...props} />
        </div>
      </ChangeHighlight>
    </Validate>
  );
};

export default Range;
